/**
 * @file DeliveryOrderCard.tsx
 * @description Delivery order ticket card for dispatch triage and driver handoff.
 */

import React from 'react';
import { MapPin, Phone, User, Clock, CheckCircle2, Truck } from 'lucide-react';
import type { DeliveryOrder, DeliveryOrderStatus } from '../../../types/dispatch.types';

interface DeliveryOrderCardProps {
  order: DeliveryOrder;
  onStatusChange: (orderId: string, status: DeliveryOrderStatus) => void;
}

const STATUS_CONFIG: Record<DeliveryOrderStatus, { label: string; badgeClass: string; nextStatus?: DeliveryOrderStatus; nextLabel?: string }> = {
  pending: {
    label: 'Pending Kitchen',
    badgeClass: 'bg-amber-950/80 text-amber-400 border-amber-800/60',
    nextStatus: 'preparing',
    nextLabel: 'Accept & Prep',
  },
  preparing: {
    label: 'Kitchen Cooking',
    badgeClass: 'bg-blue-950/80 text-blue-400 border-blue-800/60',
    nextStatus: 'ready',
    nextLabel: 'Mark Ready',
  },
  ready: {
    label: 'Ready for Driver',
    badgeClass: 'bg-purple-950 text-purple-300 border-purple-600 animate-pulse font-bold',
    nextStatus: 'out_for_delivery',
    nextLabel: 'Dispatch Driver',
  },
  assigned: {
    label: 'Driver Assigned',
    badgeClass: 'bg-indigo-950 text-indigo-300 border-indigo-600 font-bold',
    nextStatus: 'out_for_delivery',
    nextLabel: 'Dispatch Driver',
  },
  picked_up: {
    label: 'Picked Up',
    badgeClass: 'bg-sky-950 text-sky-300 border-sky-600 font-bold',
    nextStatus: 'out_for_delivery',
    nextLabel: 'Out for Delivery',
  },
  confirmed: {
    label: 'Confirmed',
    badgeClass: 'bg-amber-950/80 text-amber-400 border-amber-800/60',
    nextStatus: 'preparing',
    nextLabel: 'Start Prep',
  },
  failed: {
    label: 'Failed',
    badgeClass: 'bg-rose-950/80 text-rose-400 border-rose-800/60',
  },
  ready_for_pickup: {
    label: 'Ready for Driver',
    badgeClass: 'bg-purple-950 text-purple-300 border-purple-600 animate-pulse font-bold',
    nextStatus: 'out_for_delivery',
    nextLabel: 'Dispatch Driver',
  },
  out_for_delivery: {
    label: 'On the Way',
    badgeClass: 'bg-cyan-950 text-cyan-300 border-cyan-600 animate-pulse font-bold',
    nextStatus: 'delivered',
    nextLabel: 'Confirm Delivered',
  },
  delivered: {
    label: 'Delivered',
    badgeClass: 'bg-emerald-950/80 text-emerald-400 border-emerald-800/60',
  },
  cancelled: {
    label: 'Cancelled',
    badgeClass: 'bg-rose-950/80 text-rose-400 border-rose-800/60',
  },
};

export const DeliveryOrderCard: React.FC<DeliveryOrderCardProps> = ({ order, onStatusChange }) => {
  const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const time = new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="p-4 rounded-2xl bg-zinc-950 border border-zinc-800 hover:border-zinc-700 flex flex-col justify-between transition-all">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-base font-black text-white">{order.order_number}</span>
            <span className="text-xs text-zinc-500 font-mono">{time}</span>
          </div>
          <span className={`px-2 py-0.5 rounded-md text-[10px] uppercase font-bold border ${config.badgeClass}`}>
            {config.label}
          </span>
        </div>

        {/* Customer & Address */}
        <div className="space-y-1.5 py-2 border-y border-zinc-900 text-xs">
          <div className="flex items-center gap-2 text-zinc-200 font-semibold">
            <User className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
            <span className="truncate">{order.customer_name}</span>
          </div>
          {order.customer_phone && (
            <div className="flex items-center gap-2 text-zinc-400">
              <Phone className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
              <span>{order.customer_phone}</span>
            </div>
          )}
          <div className="flex items-start gap-2 text-zinc-400">
            <MapPin className="w-3.5 h-3.5 text-zinc-500 shrink-0 mt-0.5" />
            <span className="line-clamp-2">{order.delivery_address}</span>
          </div>
        </div>

        {/* Items Summary */}
        <div className="py-2.5 space-y-1 text-xs text-zinc-300 max-h-28 overflow-y-auto">
          {order.items.map(item => (
            <div key={item.id} className="flex items-center justify-between">
              <span>{item.quantity}× {item.name}</span>
              <span className="font-mono text-zinc-500">{(item.price * item.quantity).toFixed(2)} MAD</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer & Status Progression */}
      <div className="pt-3 border-t border-zinc-900 space-y-2.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-500">Total</span>
          <span className="font-mono font-black text-base text-amber-400">
            {order.total_amount.toFixed(2)} MAD
          </span>
        </div>

        {config.nextStatus && config.nextLabel && (
          <button
            type="button"
            onClick={() => onStatusChange(order.id, config.nextStatus!)}
            className="w-full py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 active:scale-[0.98] text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-md shadow-cyan-950/40 transition-all"
          >
            <Truck className="w-3.5 h-3.5" />
            <span>{config.nextLabel}</span>
          </button>
        )}
      </div>
    </div>
  );
};
